// Klaviyo Tracking Library
// P0 Performance: Deferred loading - only loads after user interaction

const KLAVIYO_COMPANY_ID = 'XyGCNY';

declare global {
  interface Window {
    _learnq: Array<[string, ...unknown[]]>;
    klaviyo?: {
      identify: (properties: Record<string, unknown>) => void;
      track: (event: string, properties?: Record<string, unknown>) => void;
      push: (args: unknown[]) => void;
    };
    __klaviyoLoaded?: boolean;
    __klaviyoLoadPromise?: Promise<void>;
  }
}

if (typeof window !== 'undefined') {
  window._learnq = window._learnq || [];
}

let loadPromise: Promise<void> | null = null;

/**
 * Lazy-load Klaviyo script only when needed
 * P0: Defers 3rd party script until first user interaction
 */
function loadKlaviyoScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.__klaviyoLoaded) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    if (document.querySelector(`script[src*="klaviyo"]`)) {
      window.__klaviyoLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${KLAVIYO_COMPANY_ID}`;
    script.onload = () => {
      window.__klaviyoLoaded = true;
      console.log('Klaviyo: Script loaded (deferred)');
      resolve();
    };
    script.onerror = () => {
      console.error('Klaviyo: Failed to load');
      resolve();
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Initialize Klaviyo tracking - deferred until user scroll or interaction
 */
export function initKlaviyo(companyId?: string): void {
  if (typeof window === 'undefined') return;
  
  // Defer loading until user interacts or scrolls
  const loadOnInteraction = () => {
    loadKlaviyoScript();
    window.removeEventListener('scroll', loadOnInteraction);
    window.removeEventListener('click', loadOnInteraction);
    window.removeEventListener('touchstart', loadOnInteraction);
  };

  // Use requestIdleCallback for non-blocking load, fallback to setTimeout
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      window.addEventListener('scroll', loadOnInteraction, { once: true, passive: true });
      window.addEventListener('click', loadOnInteraction, { once: true });
      window.addEventListener('touchstart', loadOnInteraction, { once: true, passive: true });
    });
  } else {
    setTimeout(() => {
      window.addEventListener('scroll', loadOnInteraction, { once: true, passive: true });
      window.addEventListener('click', loadOnInteraction, { once: true });
      window.addEventListener('touchstart', loadOnInteraction, { once: true, passive: true });
    }, 2000);
  }

  console.log('Klaviyo: Tracking initialized (deferred load)');
}

/**
 * Identify a user (after email signup, login, etc.)
 * Triggers: Welcome Series flow
 */
export function identifyUser(email: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  
  window._learnq.push(['identify', {
    $email: email,
    ...properties
  }]);

  console.log('Klaviyo: User identified', email);
}

/**
 * Track "Viewed Product" event
 * Triggers: Browse Abandonment flow
 */
export function trackViewedProduct(product: {
  productId: string;
  productName: string;
  productUrl: string;
  imageUrl: string;
  price: number;
  compareAtPrice?: number;
  currencyCode?: string;
  vendor?: string;
  categories?: string[];
  variant?: string;
}): void {
  if (typeof window === 'undefined') return;

  const item = {
    ProductID: product.productId,
    ProductName: product.productName,
    ProductURL: product.productUrl,
    ImageURL: product.imageUrl,
    Price: product.price,
    CompareAtPrice: product.compareAtPrice,
    Currency: product.currencyCode || 'SEK',
    Brand: product.vendor,
    Categories: product.categories,
    Variant: product.variant,
  };

  // Push viewed product event
  window._learnq.push(['track', 'Viewed Product', item]);

  // Also track item for catalog (needed for recommendations)
  window._learnq.push(['trackViewedItem', {
    Title: product.productName,
    ItemId: product.productId,
    Categories: product.categories,
    ImageUrl: product.imageUrl,
    Url: product.productUrl,
    Metadata: {
      Price: product.price,
      Brand: product.vendor,
    },
  }]);

  console.log('Klaviyo: Tracked Viewed Product', product.productName);
}

/**
 * Track "Added to Cart" event
 * Useful for cart abandonment (supplementary to checkout)
 */
export function trackAddedToCart(item: {
  productId: string;
  productName: string;
  productUrl: string;
  imageUrl: string;
  price: number;
  quantity: number;
  variantId: string;
  variantTitle?: string;
  currencyCode?: string;
}): void {
  if (typeof window === 'undefined') return;

  window._learnq.push(['track', 'Added to Cart', {
    $value: item.price * item.quantity,
    AddedItemProductName: item.productName,
    AddedItemProductID: item.productId,
    AddedItemSKU: item.variantId,
    AddedItemImageURL: item.imageUrl,
    AddedItemURL: item.productUrl,
    AddedItemPrice: item.price,
    AddedItemQuantity: item.quantity,
    AddedItemVariant: item.variantTitle,
    Currency: item.currencyCode || 'SEK',
    ItemNames: [item.productName],
    CheckoutURL: window.location.origin + '/cart',
    Items: [{
      ProductID: item.productId,
      ProductName: item.productName,
      Quantity: item.quantity,
      ItemPrice: item.price,
      RowTotal: item.price * item.quantity,
      ProductURL: item.productUrl,
      ImageURL: item.imageUrl,
      SKU: item.variantId,
    }],
  }]);

  console.log('Klaviyo: Tracked Added to Cart', item.productName);
}

/**
 * Track "Started Checkout" event
 * Triggers: Abandoned Checkout flow
 */
export function trackStartedCheckout(cart: {
  checkoutUrl: string;
  items: Array<{
    productId: string;
    productName: string;
    productUrl: string;
    imageUrl: string;
    price: number;
    quantity: number;
    variantId: string;
    variantTitle?: string;
  }>;
  currencyCode?: string;
}): void {
  if (typeof window === 'undefined') return;

  const totalValue = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  window._learnq.push(['track', 'Started Checkout', {
    $event_id: `checkout_${Date.now()}`,
    $value: totalValue,
    Currency: cart.currencyCode || 'SEK',
    ItemNames: cart.items.map(item => item.productName),
    CheckoutURL: cart.checkoutUrl,
    Items: cart.items.map(item => ({
      ProductID: item.productId,
      ProductName: item.productName,
      Quantity: item.quantity,
      ItemPrice: item.price,
      RowTotal: item.price * item.quantity,
      ProductURL: item.productUrl,
      ImageURL: item.imageUrl,
      SKU: item.variantId,
      Variant: item.variantTitle,
    })),
  }]);

  console.log('Klaviyo: Tracked Started Checkout', totalValue);
}

/**
 * Track custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  
  window._learnq.push(['track', eventName, properties]);
  console.log('Klaviyo: Tracked event', eventName);
}
